import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import {
  EVENT_SEVERITY_TYPE_LIST,
  SEVERITY_EVENT_FILTER_LIST,
  speedSignTagFilter,
  VIEWED_STATUS_FILTER_LIST,
} from '@app-core/constants/constants';
import { ChildConfigs, DurationRange, FleetEvents, AssetAutoCompleteResponseObject } from '@app-core/models/core.model';
import { DataService } from '@app-core/services/data/data.service';
import { DateService } from '@app-core/services/date/date.service';
import { UpdateIncidentTagsFilter } from '@app-incidents/actions/incident-filter-tags.action';
import { getIncidentsFilter, getIncidentTags, State } from '@app-incidents/reducers';
import { TaggingFilterV2Component } from '@app-shared/components/tagging-filter-v2/tagging-filter-v2.component';
import { CLIENT_CONFIG } from '@config/config';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
interface Filter {
  startDate: Date;
  endDate: Date;
  filterType: string;
  driverId: string;
  asset: AssetAutoCompleteResponseObject;
  eventType: string;
  tagIds: number[];
  selectVideoType: string;
  subEventType: string;
  viewStatus: string;
  severityType: string;
}

@Component({
  selector: 'app-page-filter',
  templateUrl: './page-filter.component.html',
  styleUrls: ['./page-filter.component.scss'],
})
export class PageFilterComponent implements OnInit, OnDestroy {
  @ViewChild('taggingFilter')
  public taggingFilterV2: TaggingFilterV2Component;

  @Input()
  set driverList(drivers) {
    this._driverList = [{ driverId: this.ALL_DRIVERS_ID }, ...drivers];
  }
  @Input()
  public currentTabIndex = 0;
  @Output()
  public incidentFilterChange = new EventEmitter<any>();

  public _driverList = [];
  private ALL_DRIVERS_ID = 'All Drivers';
  private ALL_ASSET_ID = null;
  private oldFilters: Filter;

  public durationRange: DurationRange;
  public clientConfig = CLIENT_CONFIG;
  public maxStartDate: string;
  public form = null;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public filterTypeList = [
    {
      label: 'Driver',
      value: 'driverFilter',
    },
    {
      label: 'Asset',
      value: 'assetFilter',
    },
  ];
  public customOptions = {
    showLargeInput: false,
    showAdditionalDisplayProp: true,
    additionalDisplayPropKey: 'driverName',
  };
  public incidentList = [];
  public collisionEvents;
  public modifiedEventList = [];
  public entityType = ['asset', 'driver', 'event'];
  public tagIds = [];
  public unSelectAllTag = false;
  private ngUnsubscribeOnChanges: Subject<void> = new Subject<void>();
  public incidentTags: any[];
  public uploadedEventTypeList = [
    {
      label: 'Show Uploaded Events',
      value: 'UPLOADED',
    },
    {
      label: 'Show All Events',
      value: 'allEvent',
    },
  ];
  public displaySubEventSelection: boolean;
  public displaySeverityTypeFilter: boolean;
  public subEventList = [];
  public fleetEvents: FleetEvents;
  public isOldFilter: boolean;
  public viewStatus = VIEWED_STATUS_FILTER_LIST;
  public severity = SEVERITY_EVENT_FILTER_LIST;
  private severityEventTypeKeys = Object.keys(EVENT_SEVERITY_TYPE_LIST);

  constructor(
    private store: Store<State>,
    private fb: FormBuilder,
    private dateService: DateService,
    public translate: TranslateService,
    private dataService: DataService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit() {
    const d = new Date();
    const defaultStartDate = new Date(d.setDate(d.getDate() - 7));
    this.filterChildConfigs();
    const combinedEventsList = this.dataService.modifyFleeEvents();
    this.modifiedEventList = combinedEventsList.map((k) => ({
      key: k['key'],
      label: k['label'],
      childConfigs: k['childConfigs'],
      isCollisionEvent: k.isCollisionEvent,
    }));
    this.incidentList = [
      {
        key: '',
        label: 'All',
      },
      ...this.modifiedEventList.filter((event) => !event.isCollisionEvent),
    ];
    this.collisionEvents = this.modifiedEventList.filter((e) => e.isCollisionEvent);

    this.form = this.fb.group({
      days: [7, [Validators.required]],
      startDate: [defaultStartDate, [Validators.required]],
      endDate: [new Date(), [Validators.required]],
      ...(this.clientConfig?.isAssetCentric
        ? { filterType: ['assetFilter', Validators.required] }
        : { filterType: ['driverFilter', Validators.required] }),
      driverId: [this.ALL_DRIVERS_ID],
      asset: [],
      eventType: [''],
      tagIds: [''],
      selectVideoType: ['UPLOADED'],
      subEventType: [''],
      viewStatus: ['All'],
      severity: [''],
    });

    this.form
      .get('selectVideoType')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value) => {
        if (value) {
          this.form.patchValue({ selectVideoType: 'UPLOADED' }, { emitEvent: false });
        }
      });

    this.store
      .select(getIncidentsFilter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((tripsFilter) => {
        const { days, displayStartDate: startDate, displayEndDate: endDate } = tripsFilter;
        this.durationRange = { days, startDate, endDate };
        this.form.patchValue({
          ...this.durationRange,
          filterType: tripsFilter.filterType,
          driverId: tripsFilter.driverId || this.ALL_DRIVERS_ID,
          asset: tripsFilter.asset || this.ALL_ASSET_ID,
          eventType: tripsFilter.eventType,
          tagIds: tripsFilter.tagIds,
          selectVideoType: tripsFilter.selectVideoType,
          subEventType: tripsFilter.subEventType,
          viewStatus: tripsFilter.viewStatus,
          severity: tripsFilter.severity,
        });
        this.oldFilters = {
          ...this.form.value,
          subEventType: tripsFilter.eventType === '' ? '' : tripsFilter.subEventType,
        };
        this.oldFilters.startDate = this.dateService.toDaysStart(new Date(this.oldFilters.startDate));
        this.oldFilters.endDate = this.dateService.toDaysEnd(new Date(this.oldFilters.endDate));
        this.displaySubEventSelection =
          !!this.form.get('eventType').value && this.getChildConfigs(this.form.get('eventType').value).length > 0;
        if (this.displaySubEventSelection) {
          this.subEventList = [...this.getChildConfigs(this.form.get('eventType').value).sort((a, b) => (a.label > b.label ? 1 : -1))];
          this.filterChildConfigs();
          if (this.form.get('eventType').value === 'Traffic-Speed-Violated') {
            this.subEventList = [...speedSignTagFilter, ...this.subEventList].sort((a, b) => (a.label > b.label ? 1 : -1));
          }
        } else {
          this.subEventList = [];
        }
      });

    this.store
      .select(getIncidentTags)
      .pipe(takeUntil(this.ngUnsubscribeOnChanges))
      .subscribe((tagFilter) => {
        const { tagIds } = tagFilter;
        this.tagIds = tagIds;
      });
    this.incidentTags = this.tagIds.length ? this.tagIds.map((tagid) => ({ tagId: tagid })) : [];

    this.displaySubEventSelection = !!this.form.get('eventType').value && this.getChildConfigs(this.form.get('eventType').value).length > 0;

    this.form
      .get('eventType')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.displaySubEventSelection =
          !!this.form.get('eventType').value && this.getChildConfigs(this.form.get('eventType').value).length > 0;
        if (this.displaySubEventSelection) {
          this.subEventList = [...this.getChildConfigs(this.form.get('eventType').value).sort((a, b) => (a.label > b.label ? 1 : -1))];
          this.filterChildConfigs();
          if (this.form.get('eventType').value === 'Traffic-Speed-Violated') {
            this.subEventList = [...speedSignTagFilter, ...this.subEventList].sort((a, b) => (a.label > b.label ? 1 : -1));
          }
          this.setDefaultSubEventType();
        } else {
          this.subEventList = [];
        }
        if (!!this.form.get('eventType').value.length && this.severityEventTypeKeys.includes(this.form.get('eventType').value)) {
          this.displaySeverityTypeFilter = true;
          this.form.get('severity').patchValue('');
        } else {
          this.displaySeverityTypeFilter = false;
        }
      });

    this.form
      .get('subEventType')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.form.get('severity').patchValue('');
      });

    this.form.valueChanges.pipe(takeUntil(this.ngUnsubscribe), debounceTime(10)).subscribe((values) => {
      const { startDate, endDate, eventType, subEventType } = this.form.value;

      if (startDate && endDate) {
        const updatedFormValues: Filter = {
          ...values,
          subEventType: eventType === '' ? '' : subEventType,
          startDate: this.dateService.toDaysStart(new Date(startDate)),
          endDate: this.dateService.toDaysEnd(new Date(endDate)),
        };
        this.isOldFilter = JSON.stringify(this.oldFilters) === JSON.stringify(updatedFormValues);
      }
    });
  }

  public driverChanged(driverId = '') {
    this.form.patchValue({ driverId });
  }

  public assetChanged(asset: AssetAutoCompleteResponseObject) {
    this.form.patchValue({ asset });
  }

  public getChildConfigs(eventKey: string): ChildConfigs[] {
    const selectedEvent = this.modifiedEventList.find((event) => event.key === eventKey);
    if (selectedEvent && selectedEvent.childConfigs) {
      return Object.entries(selectedEvent.childConfigs).map(([key, value]: [string, { label: string }]) => ({ key, label: value.label }));
    }
    return [];
  }

  public filterChildConfigs() {
    this.fleetEvents = this.dataService.getFleetEvents();
    if (this.fleetEvents && this.fleetEvents.standardEvents.length) {
      let fleetSubEvents = this.fleetEvents.standardEvents.filter((f) =>
        this.subEventList.some((subEvent) => subEvent.key === f.eventType)
      );
      this.subEventList = this.subEventList.filter((subEvent) => {
        const matchedSubEvvents = fleetSubEvents.find((event) => event.eventType === subEvent.key);

        // If matchedSubEvvents is undefined, it means subEvent.key is not in fleetEvents
        // If matchedSubEvvents is defined, check its state and include in subEventList accordingly
        return !matchedSubEvvents || matchedSubEvvents.state !== 'DISABLED';
      });
    }
  }

  // Method to set the default value for highlightsSubEventType
  public setDefaultSubEventType() {
    const childConfigs = this.getChildConfigs(this.form.get('eventType').value);
    if (childConfigs.length > 0) {
      this.form.get('subEventType').patchValue(childConfigs[0].key);
    }
  }

  public durationChange(durationRange: DurationRange) {
    const { days, startDate, endDate } = durationRange;
    if (days && startDate && endDate) {
      this.form?.patchValue({
        days: days,
        startDate: startDate,
        endDate: endDate,
      });
    }
  }

  /**
   * Function called on click of search button
   *
   * Emit search event.
   */
  public onSearch() {
    const {
      days,
      startDate,
      endDate,
      driverId,
      asset,
      filterType,
      eventType,
      tagIds,
      selectVideoType,
      subEventType,
      viewStatus,
      severity,
    } = this.form.value;
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    const milliSeconds = now.getMilliseconds();

    // Setting current Hours, minutes and seconds since datepicker only sets date part.
    const from = this.dateService.toDaysStartISO(new Date(startDate.setHours(hours, minutes, seconds, milliSeconds)));
    const to = this.dateService.toDaysEndISOPlusOne(new Date(endDate.setHours(hours, minutes, seconds, milliSeconds)));

    let selectedDriverId = driverId !== this.ALL_DRIVERS_ID ? driverId : '';
    let selectedAsset = asset || null;

    if (filterType === 'assetFilter') {
      selectedDriverId = undefined;
    } else {
      selectedAsset = null;
    }

    let gtmEventType = this.incidentList.find(({ key }) => key === eventType)?.label;
    this.incidentFilterChange.emit({
      days: days,
      displayStartDate: startDate,
      displayEndDate: endDate,
      paramStartDate: from,
      paramEndDate: to,
      filterType,
      driverId: selectedDriverId,
      asset: selectedAsset,
      eventType,
      gtmEventType,
      tagIds,
      selectVideoType,
      subEventType,
      viewStatus,
      ...(eventType.length ? { severity } : { severity: '' }),
    });
    this.mixpanelService.track('Safety Events Filters', {
      'Start Date': from,
      'End Date': to,
      'Filter Type': filterType,
      'Driver Id/ Name': driverId,
      'Asset Id/ Name': asset,
      'Event Type': eventType,
      'Selected Video Type': selectVideoType,
      'Sub event': subEventType,
    });
  }

  public clearForm() {
    const dateService = new DateService();
    const { from: startDate, to: endDate } = dateService.getDateRange(7);

    this.incidentFilterChange.emit({
      days: 7,
      displayStartDate: startDate,
      displayEndDate: endDate,
      paramStartDate: dateService.toDaysStartISO(startDate),
      paramEndDate: dateService.toDaysEndISOPlusOne(endDate),
      ...(this.clientConfig?.isAssetCentric ? { filterType: 'assetFilter' } : { filterType: 'driverFilter' }),
      driverId: '',
      asset: null,
      eventType: '',
      tagIds: [],
      selectVideoType: 'UPLOADED',
      viewStatus: 'All',
      severity: [],
    });
    this.taggingFilterV2.tagList = [];
    this.taggingFilterV2.tags.patchValue([]);
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.ngUnsubscribeOnChanges.next();
    this.ngUnsubscribeOnChanges.complete();
  }

  public selectedTags(tags) {
    this.store.dispatch(new UpdateIncidentTagsFilter({ tagIds: tags }));
    this.form.get('tagIds').patchValue(tags);
  }
}
